<script setup lang="ts">
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  FormDefinition,
  ShortcodeAction,
  ShortcodeDetail,
  SysOrgRoleName,
  WorkflowDefinition,
} from "~/api";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";

import { useMyInfoStore } from "~/stores/MyInfoStore";
const myInfoStore = useMyInfoStore();

import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
const entityDefinitionStore = useEntityDefinitionStore();

import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { splitByCaps } from "~/utils/miscUtils";
const orgInfoStore = useOrgInfoStore();

// For workflow and form definitions
const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updatedShortcode", shortcodeDetail: ShortcodeDetail): void;
  (e: "deleted", shortcode: ShortcodeDetail): void;
}>();

const props = defineProps<{
  shortcodeDetail: ShortcodeDetail;
  workflowDefinition: WorkflowDefinition;
  formDefinitionsWithMultipleInstances: FormDefinition[];
}>();

const shortcodeDetail = ref(props.shortcodeDetail);

const drawerOpen = ref(true);

const editing = ref(false);

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

// Only users with the setup basic role or the user who created the template
// can edit the template name
const canEdit = computed(() => {
  return (
    hasSetupBasicRole ||
    shortcodeDetail.value?.sysOrgGuid === myInfoStore.currentSysOrgGuid
  );
});

const canSave = computed(() => {
  return (
    canEdit.value &&
    !!shortcodeDetail.value?.description &&
    (!!shortcodeDetail.value?.maxUses || shortcodeDetail.value?.maxUses === 0)
  );
});

const actionOptions = computed(() => {
  return [
    {
      label: "Create Workflow",
      value: ShortcodeAction.CREATE_WORKFLOW_FOR_USER,
    },
    {
      label: "Create Form Response",
      value: ShortcodeAction.CREATE_FORM_RESPONSE_IN_WORKFLOW,
    },
  ];
});

// Computed property to handle null values for entityDefinitionGuidForNewLogin
const entityDefinitionGuidForNewLogin = computed({
  get: () => shortcodeDetail.value?.entityDefinitionGuidForNewLogin ?? "",
  set: (value: string) => {
    if (shortcodeDetail.value) {
      shortcodeDetail.value.entityDefinitionGuidForNewLogin = value || null;
    }
  },
});

// Computed property to handle null values for orgGroupGuidsForNewEntity
const orgGroupGuidsForNewEntity = computed({
  get: () => shortcodeDetail.value?.orgGroupGuidsForNewEntity ?? [],
  set: (value: string[]) => {
    if (shortcodeDetail.value) {
      shortcodeDetail.value.orgGroupGuidsForNewEntity =
        value.length > 0 ? value : null;
    }
  },
});

// Computed property to handle null values for targetFormDefinitionGuid
const targetFormDefinitionGuid = computed({
  get: () => shortcodeDetail.value?.targetFormDefinitionGuid ?? "",
  set: (value: string) => {
    if (shortcodeDetail.value) {
      shortcodeDetail.value.targetFormDefinitionGuid = value || null;
    }
  },
});

// Computed property to handle null values for createAdditionalWorkflowOnEachUse
const createAdditionalWorkflowOnEachUse = computed({
  get: () => shortcodeDetail.value?.createAdditionalWorkflowOnEachUse ?? false,
  set: (value: boolean) => {
    if (shortcodeDetail.value) {
      shortcodeDetail.value.createAdditionalWorkflowOnEachUse = value;
    }
  },
});

// Computed property to handle null values for createAdditionalFormResponseOnEachUse
const createAdditionalFormResponseOnEachUse = computed({
  get: () =>
    shortcodeDetail.value?.createAdditionalFormResponseOnEachUse ?? false,
  set: (value: boolean) => {
    if (shortcodeDetail.value) {
      shortcodeDetail.value.createAdditionalFormResponseOnEachUse = value;
    }
  },
});

// Computed property to check if workflow fields should be shown
const showWorkflowFields = computed(() => {
  return (
    shortcodeDetail.value?.action === ShortcodeAction.CREATE_WORKFLOW_FOR_USER
  );
});

// Computed property to check if form response fields should be shown
const showFormResponseFields = computed(() => {
  return (
    shortcodeDetail.value?.action ===
    ShortcodeAction.CREATE_FORM_RESPONSE_IN_WORKFLOW
  );
});

// // Load workflow definitions
// const loadWorkflowDefinitions = async () => {
//   try {
//     const sysOrgGuid = myInfoStore.currentOrg.guid;
//     const result =
//       await apiClient.workflowDefinition.getAllWorkflowDefinitionDtos(sysOrgGuid);
//     workflowDefinitions.value = result;
//   } catch (error) {
//     handleError(error);
//   }
// };

// Load form definitions - this is a fallback if we can't get forms from the workflow
// const loadFormDefinitions = async () => {
//   try {
//     // If we have a selected workflow with forms, use those
//     if (
//       selectedWorkflowDefinition.value &&
//       selectedWorkflowDefinition.value.formDefinitions &&
//       selectedWorkflowDefinition.value.formDefinitions.length > 0
//     ) {
//       formDefinitions.value = selectedWorkflowDefinition.value.formDefinitions;
//     } else {
//       // Otherwise load all forms as a fallback
//       const result = await apiClient.formDefinition.getAllFormDefinitions();
//       formDefinitions.value = result;
//     }
//   } catch (error) {
//     handleError(error);
//   }
// };

onMounted(async () => {
  if (!shortcodeDetail.value.entityDefinitionGuidForNewLogin) {
    // If the entityDefinitionGuidForNewLogin is not set, we should initialize it
    shortcodeDetail.value.entityDefinitionGuidForNewLogin =
      entityDefinitionStore.EntitiesDefinitionList[0]?.guid ?? null;
  }

  if (
    showFormResponseFields.value &&
    !shortcodeDetail.value.targetFormDefinitionGuid
  ) {
    // If the targetFormDefinitionGuid is not set, we should initialize it
    shortcodeDetail.value.targetFormDefinitionGuid =
      props.formDefinitionsWithMultipleInstances[0]?.guid ?? null;
  }
  // default the description
  if (!shortcodeDetail.value.description) {
    shortcodeDetail.value.description = splitByCaps(
      shortcodeDetail.value.action,
    );
  }
});

const updateShortcode = async () => {
  if (!canEdit.value) {
    showError("You do not have permission to update shortcodes.");
    return;
  }

  await apiClient.shortcode
    .addUpdateShortcode(shortcodeDetail.value)
    .then((res) => {
      ElMessage.success("Shortcode saved");
      editing.value = false;

      emit("updatedShortcode", res);
    })
    .catch(showError);
};

const handleClose = async () => {
  emit("closeDrawer");
};
</script>

<template>
  <el-drawer
    v-if="shortcodeDetail?.id"
    :model-value="drawerOpen"
    :title="shortcodeDetail.code || '\u00A0'"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
    class="shortcode-drawer"
    @closed="handleClose"
  >
    <template #header>
      {{
        shortcodeDetail.code
          ? "Edit Shortcode - " + shortcodeDetail.code
          : "Add a new Shortcode"
      }}
    </template>
    <div class="main">
      <p>
        A shortcode is a unique identifier that can be used to trigger specific
        actions within the system.
      </p>
      <p>
        In most cases, they will be given to people who do not yet have an
        account. When a new account is created using a shortcode, the following
        actions are performed:
      </p>
      <ul>
        <li>
          An account is created and associated with the specified entity
          definition.
        </li>
        <li>
          If provided here, the account is assigned to one or more security
          groups.
        </li>
      </ul>
      <h3>Shortcode Details</h3>
      <el-form
        v-if="shortcodeDetail"
        :model="shortcodeDetail"
        label-width="auto"
        class="form"
      >
        <el-form-item label="Action">
          <el-select
            v-model="shortcodeDetail.action"
            style="width: 300px"
            :disabled="true"
          >
            <el-option
              v-for="option in actionOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>

        <!-- Workflow-specific fields -->
        <template v-if="showWorkflowFields">
          <el-form-item label="Create multiple?">
            <div class="withComment">
              <el-switch
                v-model="createAdditionalWorkflowOnEachUse"
                :disabled="!canEdit"
              />
              <span class="comment">
                This will create another workflow for each use of this shortcode
                for each login account.
              </span>
            </div>
          </el-form-item>
        </template>

        <!-- Form Response-specific fields -->
        <template v-if="showFormResponseFields">
          <el-form-item label="Form to Create">
            <el-select
              v-model="targetFormDefinitionGuid"
              clearable
              filterable
              placeholder="Select form"
              style="width: 300px"
              :disabled="!canEdit"
            >
              <el-option
                v-for="form in props.formDefinitionsWithMultipleInstances"
                :key="form.guid"
                :label="form.name ?? undefined"
                :value="form.guid"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="Create multiple?">
            <div class="withComment">
              <el-switch
                v-model="createAdditionalFormResponseOnEachUse"
                :disabled="!canEdit"
              />
              <span class="comment">
                This will create another form response for each use of this
                shortcode for each login account.
              </span>
            </div>
          </el-form-item>
        </template>

        <el-form-item label="Description">
          <el-input
            v-model="shortcodeDetail.description"
            placeholder="Enter a description for this shortcode. (Required)"
          />
        </el-form-item>
        <el-form-item label="Max Uses">
          <div class="withComment">
            <el-input-number
              v-model="shortcodeDetail.maxUses"
              placeholder="Enter the max uses for this shortcode."
              :min="0"
              :disabled="!canEdit"
            />
            <span class="comment">
              This is the maximum number of times this shortcode can be used. If
              left at <strong>0</strong>, there is no maximum.
            </span>
          </div>
        </el-form-item>
        <h4>New Accounts</h4>

        <el-form-item label="Entity Type">
          <div>
            <div>New accounts will be created as this type of Entity:</div>
            <el-select
              v-model="entityDefinitionGuidForNewLogin"
              placeholder="Select entity type"
              style="width: 240px"
            >
              <el-option
                v-for="entity in entityDefinitionStore.EntitiesDefinitionList"
                :key="entity.guid"
                :label="entity.name ?? undefined"
                :value="entity.guid"
              />
            </el-select>
          </div>
        </el-form-item>
        <el-form-item label="Groups">
          <div>
            <div>(Optional) The entity will be added to these groups:</div>
            <el-select
              v-model="orgGroupGuidsForNewEntity"
              clearable
              multiple
              filterable
              placeholder="Select group"
              style="width: 240px"
            >
              <el-option
                v-for="group in orgInfoStore.MyOrgInfo?.orgGroupDtos"
                :key="group.guid"
                :label="group.name ?? undefined"
                :value="group.guid"
              />
            </el-select>
          </div>
        </el-form-item>

        <el-form-item class="create-button">
          <el-button
            type="primary"
            :disabled="!canSave"
            @click="updateShortcode"
            >{{ shortcodeDetail.code ? "Update" : "Create" }}</el-button
          >
        </el-form-item>
      </el-form>
    </div>
  </el-drawer>
</template>

<style lang="less">
.shortcode-drawer {
  .main {
    overflow: auto;
  }
  .withComment {
    display: flex;
    gap: 1em;
    align-items: center;
  }
}
</style>
